import React, { useEffect, useState } from 'react';
import { Row, Col, Collapse, Table, Empty } from 'antd';
import FileConstants from '../../../FileConstants';
import ENVIRONMENT from '../../../environments';
import axios from 'axios';
import { splitDateRange } from '../Utilities';
import { LoadingOutlined } from '@ant-design/icons';
import './index.scss'
import _ from "lodash" // Import the entire lodash library

const { Panel } = Collapse;
let cancelToken;

const SystemUsageSummary = (props) => {
    const [requestTypeCountReady, setRequestTypeCountReady] = useState(false);

    useEffect(() => {
        if (props.company_id || props.username) {
            setRequestTypeCountReady(false); // Reset when starting new fetch
            getDataSummary();
        }
    }, [props.company_id, props.username, props.start_date, props.end_date, props.date_filter_type, props.dataType, props.selectedProject, props.projectIDs])

    // Effect to turn off summary loader only when requestTypeCount is ready
    useEffect(() => {
        if (requestTypeCountReady) {
            console.log("Setting summary loader to false - requestTypeCount is ready")
            props.setSummaryLoader(false);
        }
    }, [requestTypeCountReady, props.requestTypeCount]);

    // Helper function to merge results from different date ranges
    const mergeResults = (existingResults, newResults, requestType) => {
        // If either result is null/undefined, return the other
        if (!existingResults) return newResults;
        if (!newResults) return existingResults;
        
        // prepareSystemUsageCounts expects an array of objects, not nested structures
        // So we need to merge the arrays directly
        if (Array.isArray(existingResults) && Array.isArray(newResults)) {
            console.log(`Merging results for ${requestType}:`, {
                existingCount: existingResults.length,
                newCount: newResults.length
            });
            
            // Create a map to track request types and their counts
            const requestTypeMap = new Map();
            
            // Process existing results
            existingResults.forEach(item => {
                if (item && item.request_type) {
                    requestTypeMap.set(item.request_type, {
                        ...item,
                        request_count: parseInt(item.request_count) || 0
                    });
                }
            });
            
            // Merge new results
            newResults.forEach(newItem => {
                if (newItem && newItem.request_type) {
                    const existingItem = requestTypeMap.get(newItem.request_type);
                    if (existingItem) {
                        // Add counts if same request type
                        existingItem.request_count += parseInt(newItem.request_count) || 0;
                        console.log(`Merged ${newItem.request_type}: ${existingItem.request_count} total`);
                    } else {
                        // Add new request type
                        requestTypeMap.set(newItem.request_type, {
                            ...newItem,
                            request_count: parseInt(newItem.request_count) || 0
                        });
                        console.log(`Added new ${newItem.request_type}: ${newItem.request_count}`);
                    }
                }
            });
            
            const mergedArray = Array.from(requestTypeMap.values());
            console.log(`Final merged result for ${requestType}:`, mergedArray.length, 'items');
            return mergedArray;
        }
        
        // If not arrays, log the issue and return the newer result
        console.warn(`Unexpected data format for ${requestType}:`, {
            existingType: typeof existingResults,
            newType: typeof newResults,
            existingIsArray: Array.isArray(existingResults),
            newIsArray: Array.isArray(newResults)
        });
        return newResults;
    };

    // Recursive function to fetch data with automatic date range splitting
    const fetchDataWithRetry = async (payload, requestType, maxRetries = 6) => {
        try {
            // Increase timeout for browser requests - browsers often need more time than Postman
            const response = await axios.post(ENVIRONMENT.GET_SYSTEM_USAGE, payload, { 
                cancelToken: cancelToken.token,
                timeout: 30000 // 30 second timeout
            });
            
            if (response?.data) {
                props.setError(prev => ({ ...prev, [requestType]: false }));
                return response.data;
            }
            return null;
        } catch (error) {
            // Check if it's a timeout or network error
            const isTimeout = error.code === 'ECONNABORTED' || 
                            error.message?.includes('timeout') ||
                            error.toString().includes('Network Error');
            
            if (isTimeout && maxRetries > 0) {
                console.log(`Timeout detected for ${requestType}, splitting date range and retrying... (${maxRetries} retries left)`);
                
                // Split the date range
                const { firstHalf, secondHalf } = splitDateRange(payload.start_date, payload.end_date);
                
                // Create payloads for both halves
                const firstPayload = { ...payload, start_date: firstHalf.start, end_date: firstHalf.end };
                const secondPayload = { ...payload, start_date: secondHalf.start, end_date: secondHalf.end };
                
                // Fetch both halves recursively
                const [firstResult, secondResult] = await Promise.all([
                    fetchDataWithRetry(firstPayload, requestType, maxRetries - 1),
                    fetchDataWithRetry(secondPayload, requestType, maxRetries - 1)
                ]);
                
                // Merge results - both should be arrays from the API
                let mergedResult = null;
                if (firstResult && secondResult) {
                    // Both results exist, merge them
                    mergedResult = mergeResults(firstResult, secondResult, requestType);
                } else if (firstResult) {
                    mergedResult = firstResult;
                } else if (secondResult) {
                    mergedResult = secondResult;
                } 
                return mergedResult;
            } else {
                // Either not a timeout or max retries reached
                if (error.toString().includes("Network Error")) {
                    props.setError(prev => ({ ...prev, [requestType]: true }));
                }
                throw error;
            }
        }
    };

    const getDataSummary = () => {
        if (typeof cancelToken != typeof undefined) {
            cancelToken.cancel("Previous Operation canceled due to new request.")
        }
        //Save the cancel token for the current request
        cancelToken = axios.CancelToken.source()
        let totalCount = {};
        let oldRequestTypeCount = {};
        props.setRequestTypeCount({});
        props.setTotalCount([]);

        props.setError({'product_models': false,
            'product_photography': false,
            'space_models': false,
            'materials': false,
            'lifestyle_photography': false,
            'managed_services': false,
            'shared_items': false,
            'scenes': false,
            'ai_models': false});
        props.setTypeLoading({ 'product_models': true,
            'product_photography': true,
            'space_models': true,
            'materials': true,
            'lifestyle_photography': true,
            'managed_services': true,
            'shared_items': true,
            'scenes': true,
            'ai_models': true});
        
        
        const requests = FileConstants.typesOfServices
            .filter((key) => key !== "managed_services" && key !== "shared_items")
            .map((key, index) => {
                let payload = {};
                payload.action = 'get_counts'
                payload.date_filter_type = props.date_filter_type;
                if (props.company_id != -1) {
                    payload.company_id = props.company_id;
                } else {
                    payload.customer_username = props.username;
                }
                payload.start_date = props.start_date;
                payload.end_date = props.end_date;
                payload.data_type = "overall";
                payload.request_type = key;

                if (props.dataType != "overall") {
                    payload.data_type = props.dataType;
                    payload.project_id = parseInt(props.selectedProject);
                } else if (props.dataType == "overall" && props.projects?.length > 0) {
                    payload.data_type = 'project_overall';
                    payload.project_id = props.projectIDs;
                }

                props.setSummaryLoader(true);

                return fetchDataWithRetry(payload, key)
                    .then(res => {
                        console.log("Res", res)
                        if (res) {
                            props.setError(prev => ({ ...prev, [key]: false }))
                            // res is now the merged array from our recursive function
                            // prepareSystemUsageCounts expects an array, so we pass res directly
                            let { request_type_count, total_count } = props.prepareSystemUsageCounts(res, totalCount, oldRequestTypeCount, () => props.setTypeLoading(prev => ({ ...prev, [key]: false })));
                            totalCount = total_count;
                            oldRequestTypeCount = request_type_count;
                        } 
                        if (!res || (Array.isArray(res) && res.length == 0)) { 
                            props.setTypeLoading(prev => ({ ...prev, [key]: false }));
                        }
                    }).catch(err => {
                        console.log(err)
                        if (err.toString().includes("Network Error")) {
                            props.setError(prev => ({ ...prev, [key]: true }))
                            props.setTypeLoading(prev => ({ ...prev, [key]: false }));                            console.error("Detected network error or CORS issue.");
                        }
                        
                        if (err instanceof Error) {
                            // Handling a generic error
                            console.error("Network error or request blocked.");
                            console.error("Full error object:", err);  // Log full error for debugging
                        } else {
                            // Handle any other unexpected errors
                            console.error("Unexpected error:", err);
                        }
                    });
            });

            Promise.all(requests).then(() => {
                console.log("Old Request Type Count")
                console.log(oldRequestTypeCount)
                setRequestTypeCountReady(true); // Set to true when all requests are done
            });
    }


    const getRequestTypeNames = (request_type) => {
        let regex = /(1k|2k|3k|4k)/;
        const containsSearchString = FileConstants.RESOLUTION_REQUESTS.some(searchString => request_type.startsWith(searchString) && !regex.test(request_type));

        if (containsSearchString) {
            const lastIndex = request_type.lastIndexOf("_");
            const beforeLastUnderscore = request_type.substring(0, lastIndex);
            const afterLastUnderscore = request_type.substring(lastIndex + 1);
            return `${FileConstants.setRequestTypeText(beforeLastUnderscore)} (${afterLastUnderscore})`;
        }
        else {
            return FileConstants.setRequestTypeText(request_type);
        }

    }
    const getCountColumns = () => [
        {
            title: <span className="manrope f-14 black-14">Request</span>,
            dataIndex: 'request_type',
            key: 'request_type',
            render: text => <span className="manrope f-12 black-14 capitalize">{getRequestTypeNames(text)}</span>,
        },
        {
            title: <span className="manrope f-14 black-14">Count</span>,
            dataIndex: 'request_count',
            key: 'request_count',
            render: text => <span className="manrope f-12 black-14">{text}</span>,
            sorter: (a, b) => a.request_count - b.request_count,
        }
    ];

    const getRowClassName = (record, index) => {
        return index % 2 === 0 ? 'even-row' : 'odd-row';
    };

    useEffect(() => {
        console.log("Summary Loader", props.summaryLoader)
        console.log("RequestTypeCountReady", requestTypeCountReady)
        console.log("RequestTypeCount", props.requestTypeCount)
    }, [props.summaryLoader, requestTypeCountReady, props.requestTypeCount])


    return <>{props.display_data ?
        props.summaryLoader ?
            <div className="justify-in-center" style={{ width: '100%' }}>
                <LoadingOutlined style={{ fontSize: 32, marginTop: 20, marginBottom: 20 }} />
            </div> :
            <Row style={{ marginLeft: 10, marginRight: 10 }}>
                <Col span={24}>
                    <Collapse style={{ marginTop: 20 }} className="collapse-panel bg-f7" ghost expandIconPosition={'right'} accordion>
                        {FileConstants.typesOfServices.map((service, index) => (
                            service == "shared_items" || service == "managed_services" ? '' :
                                <Panel
                                    showArrow={props.totalCount[service] > 0 || props.error[service] ? true : false}
                                    style={{ marginBottom: 20 }}
                                    header={<span className="manrope f-14 black-55 w-600">{FileConstants.typeOfDict(service)} {props.typeLoading[service] ? '(Loading...)' : props.error[service] ? '(Please try again with a shorter date range)' : `(${!props.totalCount[service] ? 0 : props.totalCount[service]})`}</span>}
                                    key={service}>
                                    {props.typeLoading[service] ?
                                        <div className="justify-in-center" style={{ width: '100%' }}>
                                            <LoadingOutlined style={{ fontSize: 32, marginTop: 20, marginBottom: 20 }} />
                                        </div> : props.totalCount[service] > 0 && !props.typeLoading[service] ?
                                            <Table pagination={false} columns={getCountColumns()} dataSource={props.requestTypeCount[service]}
                                                rowClassName={getRowClassName}
                                            /> : props.error[service] && !props.typeLoading[service] ?
                                                <Empty
                                                    style={{ marginTop: 20, marginBottom: 20 }}
                                                    description={"Please try again with a shorter date range"}
                                                    className="manrope f-14"
                                                /> :
                                                ''}
                                </Panel>
                        ))}

                    </Collapse>
                </Col>
            </Row> :
        <Empty
            style={{ marginTop: 20, marginBottom: 20 }}
            description={"Billing data for this month does not exist yet."}
            className="manrope f-14"
        />
    }
    </>
}

export default SystemUsageSummary;